import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { supabase, supabaseReady } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

const SupabaseSessionCtx = createContext({ ready: false, supabaseUserId: null, refresh: () => {} });

/**
 * Bridges the FastAPI session-cookie auth to a real Supabase session.
 *
 * Flow:
 *  1. AuthContext provides the FastAPI-authenticated user.
 *  2. We POST /api/supabase/session — backend ensures a shadow Supabase user
 *     exists (same UUID), signs them in server-side, returns access+refresh tokens.
 *  3. We call supabase.auth.setSession({access_token, refresh_token}) — the
 *     supabase-js client now treats this user as authenticated for Realtime + RLS.
 *  4. On FastAPI signOut, we call supabase.auth.signOut() to clear the local session.
 *
 * The bridge is best-effort: failure does not block the rest of the app.
 */
export function SupabaseSessionProvider({ children }) {
  const { user } = useAuth();
  const [supabaseUserId, setSupabaseUserId] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useMemo(() => async () => {
    if (!supabaseReady || !supabase) {
      setReady(false);
      return;
    }
    if (!user) {
      // Sign out of Supabase when FastAPI session is gone
      try { await supabase.auth.signOut(); } catch (_e) { /* noop */ }
      setSupabaseUserId(null);
      setReady(false);
      return;
    }
    try {
      const res = await api.post("/supabase/session");
      const { access_token, refresh_token, supabase_user_id } = res.data || {};
      if (!access_token || !refresh_token) {
        setReady(false);
        return;
      }
      await supabase.auth.setSession({ access_token, refresh_token });
      setSupabaseUserId(supabase_user_id);
      setReady(true);
    } catch (e) {
      // Silently fail — Supabase realtime is an enhancement, not core
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[supabase-bridge] session failed:", e?.response?.status, e?.response?.data);
      }
      setReady(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SupabaseSessionCtx.Provider value={{ ready, supabaseUserId, refresh }}>
      {children}
    </SupabaseSessionCtx.Provider>
  );
}

export function useSupabaseSession() {
  return useContext(SupabaseSessionCtx);
}
